import { useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import BackToTop from '@/components/base/BackToTop';
import { mangaData, categories } from '@/mocks/manga';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [search, setSearch] = useState('');

  const currentCat = categories.find((c) => c.slug === slug) || categories[0];

  const filtered = useMemo(() => {
    let result = slug === 'all' ? [...mangaData] : mangaData.filter((m) => m.category === currentCat.name);
    if (search.trim()) {
      result = result.filter((m) => m.title.includes(search.trim()));
    }
    return result;
  }, [slug, search, currentCat]);

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main>
        <div className="bg-background-100">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '漫画分类', path: '/category/all' },
                { label: currentCat.name },
              ]}
            />
          </div>
        </div>

        <section className="w-full px-4 md:px-6 py-8 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="mb-8">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
                {currentCat.name}漫画
              </h1>
              <p className="text-sm text-foreground-600">
                共找到 <strong>{filtered.length}</strong> 部{currentCat.name === '全部' ? '' : currentCat.name}漫画
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-8">
              <div className="flex flex-wrap gap-2">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`px-4 py-1.5 text-sm font-medium rounded-full transition-colors whitespace-nowrap ${
                      cat.slug === slug
                        ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                        : 'bg-background-100 text-foreground-700 hover:bg-primary-50 hover:text-primary-600'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>

              <div className="relative sm:ml-auto">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="搜索漫画名称..."
                  className="pl-9 pr-4 py-2 text-sm bg-background-100 border border-background-200/70 rounded-full text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors w-full sm:w-56"
                />
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400"></i>
              </div>
            </div>

            {filtered.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-background-100 rounded-full">
                  <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-600">没有找到相关漫画，试试其他关键词吧！</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5">
                {filtered.map((manga) => (
                  <Link
                    key={manga.id}
                    to={`/manga/${manga.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative overflow-hidden rounded-lg mb-3 aspect-[3/4]">
                      <img
                        src={manga.cover}
                        alt={manga.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute top-2 left-2">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-md ${
                          manga.status === '连载中'
                            ? 'bg-accent-500 text-background-50 dark:text-foreground-950'
                            : 'bg-secondary-500 text-background-50 dark:text-foreground-950'
                        }`}>
                          {manga.status}
                        </span>
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-foreground-950/80 to-transparent p-3">
                        <div className="flex items-center gap-1 text-background-100">
                          <i className="ri-star-fill text-yellow-400 text-xs"></i>
                          <span className="text-xs font-medium">{manga.rating}</span>
                        </div>
                      </div>
                    </div>
                    <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                      {manga.title}
                    </h3>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-foreground-500">{manga.author}</span>
                      <span className="text-xs text-foreground-400">·</span>
                      <span className="text-xs text-foreground-400">{manga.views}</span>
                    </div>
                    <p className="text-xs text-primary-600 mt-0.5">{manga.latestChapter}</p>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />
    </>
  );
}